import React, { cloneElement, useCallback } from 'react';
import trim from 'lodash/trim';
import { BusinessCollection } from '../../data-layer/business/constants';
import { BusinessModel } from '../../data-layer/business/types';
import { BusinessListItem, BusinessListItemSkeleton } from '../../ui/business/BusinessListItem';
import { Box } from '../../ui/layout/Box';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Flex } from '../../ui/layout/Flex';
import { Stack } from '../../ui/layout/Stack';
import { Typography } from '../../ui/typography/Typography';
import { ymTrackSearchGoToBusiness } from '../../utils/metrika';
import { useBusinessCallbacks } from '../business/useBusinessCallbacks';
import { useBusinessCollection } from '../business/useBusinessCollection';
import { useCurrentUser } from '../users/useCurrentUser';

const SKELETON_ITEMS_COUNT = 8;

interface SearchBusinessProps {
    query: string;
}

export const SearchBusiness = ({ query }: SearchBusinessProps) => {
    const collection = useBusinessCollection({
        namespace: BusinessCollection.SEARCH,
    });
    const businessCallbacks = useBusinessCallbacks();
    const currentUser = useCurrentUser();
    const onBusinessClick = useCallback(() => {
        ymTrackSearchGoToBusiness({
            page: 'Поиск',
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
        });
    }, [currentUser]);

    const getBusiness = (business?: BusinessModel) =>
        business ? (
            <BusinessListItem
                business={business}
                onClick={onBusinessClick}
                {...businessCallbacks}
            />
        ) : (
            <BusinessListItemSkeleton />
        );

    const showSkeleton =
        collection.businesses.length === 0 &&
        (collection.isLoading || collection.filters.query !== trim(query));
    const showList = showSkeleton || collection.businesses.length > 0;

    return showList ? (
        <Box
            height="100%"
            overflowY="auto"
            dataTestId={DataTestId.SEARCH_PROFILES_SCROLL_CONTAINER}
        >
            <Stack
                width="100%"
                maxWidth="100%"
                count={showSkeleton ? SKELETON_ITEMS_COUNT : collection.businesses.length}
                renderItem={(index) =>
                    cloneElement(
                        getBusiness(showSkeleton ? undefined : collection.businesses[index]),
                        {
                            key: index,
                        },
                    )
                }
            />
        </Box>
    ) : (
        <Flex
            px={3}
            alignItems="flex-start"
            justifyContent="center"
            flexGrow={1}
            spacing={1}
            dataTestId={DataTestId.SEARCH_NOT_FOUND}
        >
            <Typography variant="title2" weight="bold">
                Ничего не найдено
            </Typography>
            <Typography>Попробуйте другой запрос</Typography>
        </Flex>
    );
};
